import React, { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import { ErrorOutline as ErrorOutlineIcon, Close as CloseIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilling } from '../../context/BillingContext';
import apiClient from '../../services/apiClient';
import { softToneColor } from './freeAccessNotice';
import { formatRenewalDeadline, isRenewalInGrace } from './renewalNotice';

/**
 * #759 R — el cobro de la renovación ha fallado y aún queda semana de gracia.
 *
 * Dice hasta cuándo hay tiempo para pagar antes de perder el acceso. Quien gestiona la facturación
 * ve «Pagar ahora», que lleva a /billing/renewal (importe y enlace de Stripe); los demás miembros,
 * a quién pedírselo. Se puede seguir trabajando, así que se cierra, como el aviso de consumo, y
 * vuelve a salir en la siguiente carga mientras dure la gracia. Mientras sale, `UsageWarningBanner`
 * se aparta: ocupan el mismo sitio y este importa más.
 *
 * Solo pregunta a /api/billing/renewal (rol e importe) cuando el consumo ya dice que hay una
 * renovación sin cobrar: al resto no le cuesta ninguna petición. En las pantallas de facturación y
 * de onboarding no sale: ahí ya se habla de esto, o aún no toca.
 */
const RenewalGraceNotice = () => {
  const { usage } = useBilling();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const inGrace = isRenewalInGrace(usage);

  useEffect(() => {
    if (!inGrace) {
      setStatus(null);
      return undefined;
    }
    let cancelled = false;
    apiClient.get('/api/billing/renewal')
      .then((r) => { if (!cancelled) setStatus(r ?? null); })
      .catch((err) => {
        // Sin respuesta se avisa igual con la fecha del consumo, y el botón lleva a la pantalla
        // de pago, que decide quién puede pagar.
        console.warn('[RenewalGraceNotice] renewal status check failed:', err?.message);
        if (!cancelled) setStatus(null);
      });
    return () => { cancelled = true; };
  }, [inGrace, usage?.graceUntil]);

  if (!inGrace || dismissed) return null;
  if (pathname.startsWith('/billing/') || pathname.startsWith('/onboarding')) return null;
  // El backend sabe más que el consumo: ya pagada, o la gracia ya acabó (corta la puerta).
  if (status && (status.state !== 'unpaid' || status.blocked)) return null;

  const deadline = formatRenewalDeadline(status?.accessUntil ?? usage.graceUntil, i18n?.language);
  const markColor = softToneColor(theme, 'error');
  const isMember = status?.canManageBilling === false;

  return (
    <Paper
      elevation={6}
      data-testid="renewal-grace-notice"
      sx={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9998,
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        p: 2,
        borderRadius: 2,
        borderLeft: 4,
        borderColor: markColor,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
        <ErrorOutlineIcon sx={{ color: markColor, mt: '2px' }} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('billing.renewalGrace.title', 'No hemos podido cobrar la renovación de tu plan')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="renewal-grace-body">
            {deadline
              ? t('billing.renewalGrace.body', { date: deadline, defaultValue: 'Tienes hasta el {{date}} para pagarla; si no, perderás el acceso hasta que se pague. Tus agentes y tus conversaciones se quedan.' })
              : t('billing.renewalGrace.bodyNoDate', 'Págala para no perder el acceso. Tus agentes y tus conversaciones se quedan.')}
          </Typography>

          {isMember ? (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }} data-testid="renewal-grace-member">
              {t('billing.renewalGrace.member', 'Pide a quien gestiona la facturación de tu organización que la pague.')}
            </Typography>
          ) : (
            <Button
              color="error"
              variant="contained"
              size="small"
              disableElevation
              onClick={() => navigate('/billing/renewal')}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
              data-testid="renewal-grace-pay"
            >
              {status?.canManageBilling
                ? t('billing.renewalGrace.pay', 'Pagar ahora')
                : t('billing.renewalGrace.howToPay', 'Ver cómo pagar')}
            </Button>
          )}
        </Box>

        <Tooltip title={t('billing.warning.close', 'Cerrar')}>
          <IconButton size="small" aria-label={t('billing.warning.closeAria', 'Cerrar aviso')} onClick={() => setDismissed(true)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default RenewalGraceNotice;
